// client/src/pages/news/newsPages.js
import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';

import ogImg from '../../assets/img/og.jpg';

import Footer from '../../components/shared/Footer';
import NavBar from '../../components/shared/NavBar';
import Explore from '../../components/shared/explore.js';
import NewsTemplate from '../../components/news/newsTemplate.js';
import Description from '../../components/shared/imageDescription';

function getExperience() {
  try {
    const raw =
      window.localStorage.getItem('experience') ||
      window.localStorage.getItem('Experience') ||
      window.localStorage.getItem('siteExperience') ||
      window.localStorage.getItem('cubsExperience') ||
      window.localStorage.getItem('intranet_selected_group') ||
      'internal';

    const v = String(raw).toLowerCase();
    if (v.indexOf('seasonal') !== -1) return 'seasonal';
    if (v.indexOf('internal') !== -1) return 'internal';
    return 'internal';
  } catch (e) {
    return 'internal';
  }
}

class NewsDetailPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      transitionEnded: false,
      fallbackChrome: null
    };

    this._fallbackAttempted = false;
  }

  onTransitionEnd = () => {
    this.setState({ transitionEnded: true });
  };

  componentDidUpdate(prevProps) {
    const prevQ = prevProps.newsPageQuery;
    const q = this.props.newsPageQuery;

    // When Apollo transitions from loading->done and still has no data, fetch chrome once.
    const wasLoading = !!prevQ?.loading;
    const isLoading = !!q?.loading;

    if (q && wasLoading && !isLoading && !q.data && !this._fallbackAttempted) {
      this._fallbackAttempted = true;
      this.runFallbackChromeFetch();
    }

    // Also cover the case where q exists, not loading, no data (without relying on transition)
    if (q && !q.loading && !q.data && !this._fallbackAttempted) {
      this._fallbackAttempted = true;
      this.runFallbackChromeFetch();
    }
  }

  runFallbackChromeFetch() {
    const QUERY = `
      query newsChromeQuery {
        newsPages {
          backgroundImage { url }
        }
        exploreComponents {
          exploreTitle
          exploreTile { route title ctaText backgroundImage { url } }
        }
        globalSites {
          cubsLogo { url }
          mainNavigation {
            primaryNavigationLinks { route text }
            footerLinks { route text }
          }
        }
      }
    `;

    fetch('/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ query: QUERY })
    })
      .then((res) => res.json())
      .then((result) => {
        if (result && result.data) {
          this.setState({ fallbackChrome: result.data });
        }
      })
      .catch((err) => {
        // Keep this as console.error so it surfaces in prod logs
        console.error('Fallback chrome fetch failed:', err);
      });
  }

  /**
   * Salesforce story resolution:
   * - detail route param is the Salesforce Id (e.g. a0Y...)
   * - wrappers sometimes pass a single object (props.story) or arrays (props.news / props.newsItems)
   */
  getSalesforceStoryFromProps() {
    const articleId = this.props.match?.params?.news;

    if (this.props.story && typeof this.props.story === 'object') return this.props.story;

    if (Array.isArray(this.props.news) && this.props.news.length) {
      const hit = this.props.news.find((n) => n?.id === articleId) || this.props.news[0];
      return hit || null;
    }

    if (Array.isArray(this.props.newsItems) && this.props.newsItems.length) {
      const hit = this.props.newsItems.find((n) => n?.id === articleId) || this.props.newsItems[0];
      return hit || null;
    }

    return null;
  }

  renderMinimalHeader(heroClasses) {
    return (
      <div id="home" className={heroClasses}>
        <Helmet>
          <meta property="og:image" content={ogImg} />
        </Helmet>
        <header id="site-header" className="container" role="banner">
          <div style={{ padding: '16px 0' }}>
            <a href="/" style={{ textDecoration: 'none' }}>
              <h1 style={{ margin: 0, fontSize: 22 }}>Cubs News</h1>
            </a>
          </div>
        </header>
        <main className="container" style={{ padding: 24 }}>
          <div>Loading…</div>
        </main>
      </div>
    );
  }

  render() {
    const heroClasses = classnames('hero', { animate: this.state.transitionEnded });

    const q = this.props.newsPageQuery || null;
    const isLoading = !!q?.loading;

    // Chrome comes from Apollo (Contentful) when available, else fallback.
    const chromeData = q?.data || this.state.fallbackChrome || null;

    // If we have neither chrome nor a SF story yet and we’re loading, show a minimal shell.
    const sfStory = this.getSalesforceStoryFromProps();
    if (isLoading && !chromeData && !sfStory) {
      return this.renderMinimalHeader(heroClasses);
    }

    if (sfStory && getExperience() === 'seasonal') {
      if (this.props.history && this.props.history.replace) {
        this.props.history.replace('/cubs-news');
      } else {
        window.location.href = '/cubs-news';
      }
      return null;
    }

    // Build chrome
    const site0 = chromeData?.globalSites?.[0] || null;
    const mainNav = site0?.mainNavigation || null;
    const cubsLogoUrl = site0?.cubsLogo?.url || null;
    const backgroundImageUrl = chromeData?.newsPages?.[0]?.backgroundImage?.url || null;

    return (
      <div id="home">
        <div key="interior news-detail" className={heroClasses}>
          <Helmet>
            <meta property="og:image" content={ogImg} />
          </Helmet>

          {/* Header */}
          {site0 ? (
            <header id="site-header" className="container" role="banner">
              <a id="site-title" className="h1" href="/">
                {cubsLogoUrl ? <img src={cubsLogoUrl} alt="site-header" /> : <span>Cubs</span>}
              </a>

              {backgroundImageUrl ? (
                <img src={backgroundImageUrl} alt="background" className="featured-image" />
              ) : null}

              {mainNav ? <NavBar NavBar={mainNav} /> : null}

              <div className="header-content">
                <div className="row2 padding-bottom">
                  <Description description={chromeData?.newsPages || []} />
                </div>
              </div>
            </header>
          ) : (
            <header id="site-header" className="container" role="banner">
              <div style={{ padding: '16px 0' }}>
                <a href="/" style={{ textDecoration: 'none' }}>
                  <h1 style={{ margin: 0, fontSize: 22 }}>Cubs News</h1>
                </a>
              </div>
            </header>
          )}

          <main className="container">
            {/* Content */}
            {isLoading && !sfStory ? (
              <div style={{ padding: 24 }}>Loading…</div>
            ) : sfStory ? (
              <NewsTemplate news={sfStory} />
            ) : (
              <div style={{ padding: 24 }}>
                <h2>Article not found</h2>
                <p>The requested article could not be located.</p>
              </div>
            )}

            {/* Explore */}
            {chromeData?.exploreComponents?.[0] ? (
              <Explore explore={chromeData.exploreComponents[0]} />
            ) : null}
          </main>

          {mainNav ? <Footer footer={mainNav} /> : null}
        </div>
      </div>
    );
  }
}

export default withRouter(NewsDetailPage);
